import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import apiClient from '../utils/apiClient';
import ProfilePicture from './shared/ProfilePicture';
import { ModalShell, ModalActions, ModalSection } from './modals/core';
import {
  UserPlusIcon,
  UserIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';

interface Guest {
  mg_nr: number;
  vorname: string;
  name: string;
  has_booked?: boolean;
  cards?: Array<{
    id: number;
    remaining_entries: number;
  }>;
}

interface AddGuestModalProps {
  event: any;
  isOpen: boolean;
  onClose: () => void;
  onBook: (eventId: number, count: number) => void;
  checkBooking?: () => void;
  refreshEvent?: () => void;
}

const AddGuestModal: React.FC<AddGuestModalProps> = ({
  event,
  isOpen,
  onClose,
  onBook,
  checkBooking,
  refreshEvent
}) => {
  const { config } = useContext(AuthContext);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [selectedMember, setSelectedMember] = useState('');
  const [loading, setLoading] = useState({
    fetchGuests: false,
    addGuest: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const BASE_URL = process.env.REACT_APP_API_URL || config?.apiUrl || 'http://localhost:4000';

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
        onClose();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, onClose]);

  // Fetch guests with cards
  const fetchGuests = async () => {
    if (!event) return;
    setLoading(prev => ({ ...prev, fetchGuests: true }));
    try {
      const response = await apiClient.get(`/api/guest-cards/guests-with-cards?eventId=${event.id}`);
      if (response.data.success) {
        setGuests(response.data.guests || []);
      } else {
        setError('Keine Gäste gefunden');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(`Fehler beim Laden der Gäste: ${err.message}`);
      }
    } finally {
      setLoading(prev => ({ ...prev, fetchGuests: false }));
    }
  };

  const handleAddMember = async () => {
    setLoading(prev => ({ ...prev, addGuest: true }));
    const memberId = Number(selectedMember);
    const guest = guests.find(g => g.mg_nr === memberId);

    try {
      if (guest && guest.cards && guest.cards.length > 0) {
        const availableCard = guest.cards.find(card => card.remaining_entries > 0);
        if (!availableCard) {
          setError('Keine verfügbaren Tickets auf der Gästekarte');
          return;
        }
        await handleGuestBooking(memberId, availableCard);
      } else {
        setError('Kein Gast oder keine Gästekarte verfügbar');
      }
    } finally {
      setLoading(prev => ({ ...prev, addGuest: false }));
    }
  };

  const handleGuestBooking = async (memberId: number, guestCard: any) => {
    try {
      const response = await apiClient.post('/api/members/book-event', {
        guest_card_id: guestCard.id,
        kalender_id: event.id,
        guest_id: memberId
      });

      if (response.data.success) {
        const guest = guests.find(g => g.mg_nr === memberId);
        setSuccessMessage(`Termin für ${guest?.vorname} ${guest?.name} erfolgreich gebucht!`);
        setSelectedMember('');

        if (checkBooking) await checkBooking();
        if (refreshEvent) await refreshEvent();

        const bookingCount = response.data.booking_count || event.extendedProps?.bookingCount || 0;
        onBook(event.id, Math.min(bookingCount, event.extendedProps?.bookable || bookingCount));
      } else {
        setError(response.data.message || 'Buchung fehlgeschlagen');
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(`Fehler beim Buchen: ${err.message}`);
      }
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchGuests();
    }
  }, [isOpen, event]);

  return (
    <ModalShell
      open={isOpen}
      onClose={() => onClose()}
      variant="choice"
      size="md"
      icon={UserPlusIcon}
      title="Gast hinzufügen"
      closeOnBackdrop={false}
      footer={
        <ModalActions
          primary={{
            label: loading.addGuest ? 'Wird hinzugefügt...' : 'Gast zum Termin hinzufügen',
            onClick: handleAddMember,
            disabled: !selectedMember || loading.addGuest,
            loading: loading.addGuest,
            icon: !loading.addGuest ? <UserPlusIcon className="h-5 w-5" /> : undefined,
          }}
        />
      }
    >
      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl">
          <div className="flex items-center gap-3">
            <ExclamationCircleIcon className="h-5 w-5 text-error-500 flex-shrink-0" />
            <span className="text-sm font-medium text-error-700 dark:text-error-300">{error}</span>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl">
          <div className="flex items-center gap-3">
            <CheckCircleIcon className="h-5 w-5 text-success-500 flex-shrink-0" />
            <span className="text-sm font-medium text-success-700 dark:text-success-300">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Guest Selection */}
      <ModalSection>
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center gap-2">
          <UserIcon className="h-4 w-4" />
          Verfügbare Gäste auswählen:
        </p>

        {loading.fetchGuests ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-2 border-primary-600 border-t-transparent rounded-full mr-3" />
            <span className="text-sm text-gray-600 dark:text-gray-400">Gäste werden geladen...</span>
          </div>
        ) : guests.length === 0 ? (
          <div className="text-center py-12">
            <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Keine Gäste verfügbar</p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Alle Gäste haben bereits gebucht oder keine gültigen Karten
            </p>
          </div>
        ) : (
          <div className="max-h-[40vh] overflow-y-auto">
            <div className="space-y-3">
              {guests.map((guest) => {
                const hasAvailableTickets = guest.cards && guest.cards.length > 0 &&
                  guest.cards.some(card => card.remaining_entries > 0);
                const totalTickets = guest.cards ?
                  guest.cards.reduce((sum, card) => sum + card.remaining_entries, 0) : 0;
                const isDisabled = guest.has_booked || !hasAvailableTickets;
                const isSelected = selectedMember === guest.mg_nr.toString();

                return (
                  <button
                    key={guest.mg_nr}
                    onClick={() => !isDisabled && setSelectedMember(guest.mg_nr.toString())}
                    disabled={isDisabled}
                    className={`w-full min-h-[56px] flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 transform ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 scale-[1.02]'
                        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                    } ${
                      isDisabled
                        ? 'opacity-60 cursor-not-allowed'
                        : 'hover:shadow-md cursor-pointer hover:scale-[1.01] active:scale-[0.99]'
                    }`}
                  >
                    <ProfilePicture guest={guest} />

                    <div className="flex-1 text-left">
                      <p className={`text-sm font-semibold ${
                        isDisabled ? 'text-gray-500 dark:text-gray-500' : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {guest.vorname} {guest.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {hasAvailableTickets
                            ? `${totalTickets} Ticket${totalTickets !== 1 ? 's' : ''} verfügbar`
                            : guest.cards && guest.cards.length > 0
                            ? 'Alle Tickets aufgebraucht'
                            : 'Keine Gästekarte vorhanden'
                          }
                        </p>
                        {guest.has_booked && (
                          <span className="text-xs font-medium bg-error-100 dark:bg-error-900/30 text-error-600 dark:text-error-400 px-2 py-0.5 rounded-full">
                            Bereits gebucht
                          </span>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <CheckCircleIcon className="h-6 w-6 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </ModalSection>
    </ModalShell>
  );
};

export default AddGuestModal;
